import { useMemo, useState } from "react";

import {
  TIDAL_LEVELS,
  TIDAL_MEASUREMENTS,
  applySecondaryCorrection,
  formatTime,
  heightByTwelfths,
  heightSinusoid,
  parseTime,
  timeForHeight,
} from "@nav-games/tides";

import { ChartDatumsFigure } from "../../ui/ChartDatumsFigure";
import { TidalCurve } from "../../ui/TidalCurve";
import { TidalHeightsFigure } from "../../ui/TidalHeightsFigure";

interface TidesProps {
  onNavigate: (to: string) => void;
}

export function Tides({ onNavigate }: TidesProps) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>Tides — heights and datums</h2>
      <p className="muted">
        How tidal heights, charted depths, drying features, and
        vertical clearances all relate to one another, plus the
        three calculators every RYA candidate needs: rule of
        twelfths, secondary-port corrections, and tidal-curve
        interpolation.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Play</h3>
        <button onClick={() => onNavigate("/play/tides/levels")}>
          Name the tidal levels — drag &amp; drop
        </button>
        <p className="small muted" style={{ margin: "8px 0 16px" }}>
          Drop each abbreviation (HAT, MHWS, MHWN, ML, MLWN, MLWS, LAT,
          CD) onto the right horizontal line on the cross-section.
        </p>
        <button
          onClick={() => onNavigate("/play/tides/twelfths")}
          className="secondary"
        >
          Rule of twelfths — flash drill
        </button>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          Multiple-choice: given HW + LW heights and an HW + n hour
          offset, pick the tide height. Reveal shows the full
          twelfths working.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Which datum is the chart figure from?</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Every figure printed on an Admiralty chart is measured
          either from <strong>MHWS</strong> (heights — bridges,
          lights, hilltops) or <strong>Chart Datum</strong> (depths
          and drying heights). The arrows below pair each chart
          annotation with the datum it's referenced to.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ChartDatumsFigure size={620} />
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Tide-level cross-section</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The diagram is anchored to <strong>Chart Datum (CD)</strong> —
          the zero level on every Admiralty chart. Eight horizontal
          lines mark the standard tidal levels; the blue line marks
          an example "now" tide between MHWN and MHWS, with the
          resulting sounding shown in green.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <TidalHeightsFigure size={620} />
        </div>
      </div>

      <TwelfthsCalculator />

      <SecondaryPortCalculator />

      <InterpolationCalculator />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Tidal levels (top to bottom)</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          The eight standard datums in the order they appear on the
          diagram.
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {TIDAL_LEVELS.map((l) => (
              <tr key={l.abbreviation} style={{ verticalAlign: "top" }}>
                <td
                  style={{
                    padding: "8px 8px 8px 0",
                    width: 70,
                    fontWeight: 700,
                  }}
                >
                  {l.abbreviation}
                </td>
                <td style={{ padding: "8px" }}>
                  <div style={{ fontWeight: 600 }}>{l.name}</div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {l.description}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Measurements + relationships</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          What each annotation arrow on the diagram represents.
          Memorise the one identity: <strong>sounding = charted
          depth + height of tide</strong>. Almost every tides
          calculation reduces to that line.
        </p>
        {TIDAL_MEASUREMENTS.map((m) => (
          <details key={m.id} className="card" style={{ marginBottom: 6 }}>
            <summary
              style={{
                cursor: "pointer",
                listStyle: "none",
                fontWeight: 600,
              }}
            >
              {m.name}
              {m.abbreviation && (
                <span className="small muted" style={{ marginLeft: 8 }}>
                  ({m.abbreviation})
                </span>
              )}
            </summary>
            <p className="small muted" style={{ margin: "8px 0 4px" }}>
              {m.definition}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Reference:</strong> {m.reference}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>On the chart:</strong> {m.shownOnChart}
            </p>
          </details>
        ))}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}

// ─── Rule of twelfths ──────────────────────────────────────────

function TwelfthsCalculator() {
  const [hwTime, setHwTime] = useState("12:00");
  const [hwHeight, setHwHeight] = useState("5.2");
  const [lwTime, setLwTime] = useState("18:15");
  const [lwHeight, setLwHeight] = useState("1.4");
  const [queryTime, setQueryTime] = useState("15:00");

  const result = useMemo(() => {
    const hwM = parseTime(hwTime);
    const lwM = parseTime(lwTime);
    const qM = parseTime(queryTime);
    const hwH = parseFloat(hwHeight);
    const lwH = parseFloat(lwHeight);
    if (
      hwM === null ||
      lwM === null ||
      qM === null ||
      Number.isNaN(hwH) ||
      Number.isNaN(lwH)
    ) {
      return null;
    }
    const lwAfterHw = lwM <= hwM ? lwM + 1440 : lwM;
    const qAfterHw = qM < hwM ? qM + 1440 : qM;
    const durationH = (lwAfterHw - hwM) / 60;
    const hoursFromHw = (qAfterHw - hwM) / 60;
    if (hoursFromHw < 0 || hoursFromHw > durationH) {
      return {
        twelfths: null as number | null,
        sinusoid: null as number | null,
        hoursFromHw,
        durationH,
        error:
          "Query time is outside this HW → LW window. Use the next cycle.",
      };
    }
    return {
      twelfths: heightByTwelfths(hwH, lwH, hoursFromHw, durationH),
      sinusoid: heightSinusoid(hwH, lwH, hoursFromHw, durationH),
      hoursFromHw,
      durationH,
      error: null as string | null,
    };
  }, [hwTime, hwHeight, lwTime, lwHeight, queryTime]);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Rule of twelfths — height at any time</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Enter HW + LW for the cycle you're working in, plus the time
        you want the height for. Returns both the rule-of-twelfths
        approximation (RYA standard) and the more accurate
        sinusoidal curve below.
      </p>
      <CalcGrid>
        <Field label="HW time" value={hwTime} onChange={setHwTime} placeholder="hh:mm" />
        <Field label="HW height (m)" value={hwHeight} onChange={setHwHeight} placeholder="5.2" />
        <Field label="LW time" value={lwTime} onChange={setLwTime} placeholder="hh:mm" />
        <Field label="LW height (m)" value={lwHeight} onChange={setLwHeight} placeholder="1.4" />
        <Field label="Time wanted" value={queryTime} onChange={setQueryTime} placeholder="hh:mm" />
      </CalcGrid>
      {result?.error && (
        <p className="small" style={{ color: "var(--danger)", margin: "8px 0 0" }}>
          {result.error}
        </p>
      )}
      {result && !result.error && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginTop: 16,
              padding: 12,
              borderRadius: 6,
              background: "var(--accent-soft)",
            }}
          >
            <div>
              <div className="small muted">Twelfths</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {result.twelfths!.toFixed(2)} m
              </div>
            </div>
            <div>
              <div className="small muted">Sinusoid</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {result.sinusoid!.toFixed(2)} m
              </div>
            </div>
          </div>
          <p className="small muted" style={{ margin: "8px 0 12px" }}>
            {result.hoursFromHw.toFixed(2)} hours after HW out of a{" "}
            {result.durationH.toFixed(2)}-hour HW → LW cycle.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <TidalCurve
              hwHeight={parseFloat(hwHeight)}
              lwHeight={parseFloat(lwHeight)}
              cycleDurationHours={result.durationH}
              queryHoursFromHw={result.hoursFromHw}
              size={420}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Secondary-port correction ──────────────────────────────────

function SecondaryPortCalculator() {
  const [stdHwTime, setStdHwTime] = useState("12:00");
  const [stdHwH, setStdHwH] = useState("5.2");
  const [stdLwTime, setStdLwTime] = useState("18:15");
  const [stdLwH, setStdLwH] = useState("1.4");
  const [diffHwMin, setDiffHwMin] = useState("-42");
  const [diffLwMin, setDiffLwMin] = useState("-35");
  const [diffHwH, setDiffHwH] = useState("-0.6");
  const [diffLwH, setDiffLwH] = useState("-0.2");

  const corrected = useMemo(() => {
    const hwM = parseTime(stdHwTime);
    const lwM = parseTime(stdLwTime);
    const hwH = parseFloat(stdHwH);
    const lwH = parseFloat(stdLwH);
    if (
      hwM === null ||
      lwM === null ||
      Number.isNaN(hwH) ||
      Number.isNaN(lwH)
    ) {
      return null;
    }
    const dHw = parseInt(diffHwMin, 10);
    const dLw = parseInt(diffLwMin, 10);
    const dHwH = parseFloat(diffHwH);
    const dLwH = parseFloat(diffLwH);
    if (
      Number.isNaN(dHw) ||
      Number.isNaN(dLw) ||
      Number.isNaN(dHwH) ||
      Number.isNaN(dLwH)
    ) {
      return null;
    }
    return applySecondaryCorrection(
      { hwTimeMins: hwM, hwHeight: hwH, lwTimeMins: lwM, lwHeight: lwH },
      {
        hwTimeMins: dHw,
        lwTimeMins: dLw,
        hwHeight: dHwH,
        lwHeight: dLwH,
      },
    );
  }, [stdHwTime, stdHwH, stdLwTime, stdLwH, diffHwMin, diffLwMin, diffHwH, diffLwH]);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Secondary-port correction</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Apply the time + height differences from the almanac to a
        standard-port HW / LW prediction. Differences are <em>signed
        offsets</em> — negative time = secondary HW is earlier;
        negative height = secondary HW / LW is lower.
      </p>
      <p
        className="small"
        style={{
          margin: "0 0 10px",
          fontWeight: 600,
          color: "var(--text-muted)",
        }}
      >
        Standard port
      </p>
      <CalcGrid>
        <Field label="HW time" value={stdHwTime} onChange={setStdHwTime} />
        <Field label="HW height (m)" value={stdHwH} onChange={setStdHwH} />
        <Field label="LW time" value={stdLwTime} onChange={setStdLwTime} />
        <Field label="LW height (m)" value={stdLwH} onChange={setStdLwH} />
      </CalcGrid>
      <p
        className="small"
        style={{
          margin: "16px 0 10px",
          fontWeight: 600,
          color: "var(--text-muted)",
        }}
      >
        Secondary-port differences
      </p>
      <CalcGrid>
        <Field label="HW time diff (min)" value={diffHwMin} onChange={setDiffHwMin} />
        <Field label="HW height diff (m)" value={diffHwH} onChange={setDiffHwH} />
        <Field label="LW time diff (min)" value={diffLwMin} onChange={setDiffLwMin} />
        <Field label="LW height diff (m)" value={diffLwH} onChange={setDiffLwH} />
      </CalcGrid>
      {corrected && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 6,
            background: "var(--accent-soft)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <div className="small muted">Secondary HW</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {formatTime(corrected.hwTimeMins)} ·{" "}
              {corrected.hwHeight.toFixed(2)} m
            </div>
          </div>
          <div>
            <div className="small muted">Secondary LW</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {formatTime(corrected.lwTimeMins)} ·{" "}
              {corrected.lwHeight.toFixed(2)} m
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Time-for-height interpolation ─────────────────────────────

function InterpolationCalculator() {
  const [hwTime, setHwTime] = useState("12:00");
  const [hwHeight, setHwHeight] = useState("5.2");
  const [lwTime, setLwTime] = useState("18:15");
  const [lwHeight, setLwHeight] = useState("1.4");
  const [targetHeight, setTargetHeight] = useState("3.0");

  const result = useMemo(() => {
    const hwM = parseTime(hwTime);
    const lwM = parseTime(lwTime);
    const hwH = parseFloat(hwHeight);
    const lwH = parseFloat(lwHeight);
    const targetH = parseFloat(targetHeight);
    if (
      hwM === null ||
      lwM === null ||
      Number.isNaN(hwH) ||
      Number.isNaN(lwH) ||
      Number.isNaN(targetH)
    ) {
      return null;
    }
    const lwAfterHw = lwM <= hwM ? lwM + 1440 : lwM;
    const durationH = (lwAfterHw - hwM) / 60;
    const tFromHw = timeForHeight(hwH, lwH, targetH, durationH);
    if (tFromHw === null) {
      return {
        error: `Target ${targetH.toFixed(1)} m is outside HW (${hwH}) / LW (${lwH}) range.`,
        time: null as string | null,
        hoursFromHw: null as number | null,
        durationH,
      };
    }
    return {
      time: formatTime(hwM + tFromHw * 60),
      hoursFromHw: tFromHw,
      durationH,
      error: null as string | null,
    };
  }, [hwTime, hwHeight, lwTime, lwHeight, targetHeight]);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Time at a given height</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        The inverse problem: "when will the tide be at 3.0 m?" Useful
        for working out the safest time to cross a sill or enter a
        drying harbour. Uses the sinusoidal curve — more accurate
        than reading twelfths off the back of an envelope.
      </p>
      <CalcGrid>
        <Field label="HW time" value={hwTime} onChange={setHwTime} />
        <Field label="HW height (m)" value={hwHeight} onChange={setHwHeight} />
        <Field label="LW time" value={lwTime} onChange={setLwTime} />
        <Field label="LW height (m)" value={lwHeight} onChange={setLwHeight} />
        <Field
          label="Target height (m)"
          value={targetHeight}
          onChange={setTargetHeight}
        />
      </CalcGrid>
      {result?.error && (
        <p className="small" style={{ color: "var(--danger)", margin: "8px 0 0" }}>
          {result.error}
        </p>
      )}
      {result && !result.error && (
        <>
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 6,
              background: "var(--accent-soft)",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <div className="small muted">Tide reaches target at</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{result.time}</div>
            </div>
            <div>
              <div className="small muted">After HW</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {result.hoursFromHw!.toFixed(2)} h
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <TidalCurve
              hwHeight={parseFloat(hwHeight)}
              lwHeight={parseFloat(lwHeight)}
              cycleDurationHours={result.durationH}
              queryHoursFromHw={result.hoursFromHw!}
              size={420}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared form bits ──────────────────────────────────────────

function CalcGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="small muted">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 15,
        }}
      />
    </label>
  );
}
